
import { useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import InputForm from "../input/input";
import DateMasked from "../inputMask/DateMasked";
import Button from "../button/button";
import ButtonRadio from "../button-radio/button-radio";
import DropDown from "../dropdown/dropdown";

const deepEqual = (a, b) => {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (a && b && typeof a === "object") {
    // Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    // Objetos
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const k of aKeys) {
      if (!deepEqual(a[k], b[k])) return false;
    }
    return true;
  }

  // NaN === NaN
  if (Number.isNaN(a) && Number.isNaN(b)) return true;

  return false;
};


const FilterBar = ({
  data = {},
  filters = {},
  onFiltersChange,
  schema = [],
  columns,
  initialFilters,
}) => {
  const updateFilter = (key, value, normalizeValue) => {
    const nextValue = normalizeValue ? normalizeValue(value) : value;
    onFiltersChange((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  const resolvedOptions = useMemo(() => {
    // Resolve e memoiza opções por chave do filtro
    const res = {};
    schema.forEach((field) => {
      if (Array.isArray(field.options)) {
        res[field.key] = field.options;
      } else if (typeof field.options === "string") {
        const raw = data[field.options] || [];
        res[field.key] = Array.isArray(raw)
          ? raw.map(field.mapOption || defaultMap(field.options))
          : [];
      } else if (typeof field.options === "function") {
        const rawList = field.options(data);
        res[field.key] = Array.isArray(rawList)
          ? rawList.map(field.mapOption || ((x) => x))
          : [];
      } else {
        res[field.key] = [];
      }
    });
    return res;
  }, [data, schema]);

  // Detecta se há qualquer filtro ativo (para mostrar botão "Limpar")

  const hasAnyFilter = useMemo(() => {
    return !deepEqual(filters, initialFilters);
  }, [filters, initialFilters]);

  // Limpa voltando ao objeto inicial, preservando tipos (boolean, null, arrays, strings)
  const clearFilters = () => {
    onFiltersChange(initialFilters);
  };
  return (
    <div className={`grid sm:grid-cols-1 md:grid-cols-${columns} lg:grid-cols-${columns} gap-3 mt-4`}>
      {schema.map((field) => {
        const colSpan = field.colSpan ?? 2;
        const value = filters[field.key];
        const options = resolvedOptions[field.key] || [];
        const commonLabelClass = "block text-sm font-medium text-foreground/80 mb-1.5";
        const commonInputClass = "w-full text-xs";
        return (
          <div key={field.key} className={`col-span-${colSpan}`}>

            {field.component === "MultiSelect" && (
              <MultiSelect
                value={value || []}
                label={field.label}
                disabled={field.disabled}
                options={options}
                onChange={(e) =>
                  updateFilter(field.key, e.value, field.normalizeValue)
                }
                placeholder={field.placeholder}
                display={field.display || "chip"}
                className={commonInputClass}
                filter={field.filter ?? true}
                showClear={field.showClear ?? true}
              />
            )}

            {field.component === "Checkbox" && (
              <div className="flex gap-4 items-center h-full">
                {options.map((opt) => {
                  const checked = (value || []).includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-1 text-sm ">
                      <input
                        type="checkbox"
                        checked={checked}
                        className="w-4 h-4 border border-input rounded accent-primary focus:ring-2 focus:ring-primary/20"
                        onChange={(e) => {
                          let nextValues = value || [];
                          if (e.target.checked) {
                            nextValues = [...nextValues, opt.value];
                          } else {
                            nextValues = nextValues.filter((v) => v !== opt.value);
                          }
                          updateFilter(field.key, nextValues, field.normalizeValue);
                        }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}

            {field.component === "Radio" && (
              <div className="flex gap-4">
                {options.map((opt) => {
                  const checked = value === opt.value; // ✅ Comparação correta
                  const name = field.name || field.key;
                  return (
                    <label key={String(opt.value)} className="flex items-center gap-2 text-sm pt-2">
                      <ButtonRadio
                        type="radio"
                        label={opt.label}
                        name={name}
                        value={String(opt.value)}
                        checked={checked}
                        onChange={() => updateFilter(field.key, opt.value)}
                      />
                    </label>
                  );
                })}
              </div>
            )}

            {field.component === "Dropdown" && (
              <DropDown
                value={value}
                label={field.label}
                options={options}
                disabled={field.disabled}
                optionLabel={field.optionLabel || "label"}
                optionValue={field.optionValue || "value"}
                onChange={(e) =>
                  updateFilter(field.key, e.value, field.normalizeValue)
                }
                placeholder={field.placeholder || "Selecione..."}
                className={commonInputClass}
              />
            )}

            {field.component === "Input" && (
              <InputForm
                type={field.typeText}
                value={value}
                label={field.label}
                disabled={field.disabled}
                onChange={(e) =>
                  updateFilter(field.key, e.target.value)
                }
                placeholder={field.placeholder}
                className={commonInputClass}
              />
            )}

            {field.component === "Data" && (
              <DateMasked
                id="dataNasc"
                name="dataNasc"
                disabled={field.disabled}
                value={value}
                onChange={(e) =>
                  updateFilter(field.key, e)
                }
                min="1900-01-01"
                max="2100-12-31"
              />
            )}

          </div>
        );
      })}

      {hasAnyFilter && (
        <div className="flex items-end">
          <Button
            label="Limpar"
            iconLeft="fa fa-eraser"
            color="gray-200"
            textColor="gray"
            onClick={clearFilters}
          />
        </div>
      )}
    </div>
  );
};

// Mapeadores padrão: se vier do data como array de strings, vira {label, value}.
// Se vier com {codigo, descricao}, vira "codigo - descricao".
const defaultMap =
  (sourceKey) =>
    (item) => {
      if (
        item &&
        typeof item === "object" &&
        "codigo" in item &&
        "descricao" in item
      ) {
        return {
          label: `${item.codigo} - ${item.descricao}`,
          value: item.codigo,
        };
      }
      if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
        return { label: String(item), value: item };
      }
      return item;
    };

export default FilterBar;